import { Link } from "@tanstack/react-router";
import { Award, Plus, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { fetchFileDownloadUrl, useFileUpload } from "@/hooks/use-file-upload";
import type { PersonCertificationRow } from "@/hooks/use-persons";
import {
  useDeletePersonCertification,
  usePersonCertifications,
  useUpdatePersonCertification,
} from "@/hooks/use-persons";
import { formatDateOnly, isExpiredDateOnly } from "@/lib/dates";
import { originLinkSearch } from "@/lib/detail-origin";
import { PersonCertificationAssignDialog } from "./PersonCertificationAssignDialog";

const statusToneMap: Record<PersonCertificationRow["status"], StatusTone> = {
  assigned: "neutral",
  started: "info",
  completed: "success",
};

export function PersonCertificationsSection({
  personId,
  personName,
  isAdmin,
}: {
  personId: string;
  personName: string;
  isAdmin: boolean;
}) {
  const { data: certs = [], isLoading, isError, refetch } = usePersonCertifications(personId);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PersonCertificationRow | null>(null);
  const update = useUpdatePersonCertification(personId);
  const remove = useDeletePersonCertification(personId);

  return (
    <DetailSection
      title="Certifications"
      headerActions={
        isAdmin && (
          <Button variant="ghost" size="sm" onClick={() => setAssignOpen(true)}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Assign
          </Button>
        )
      }
    >
      {isLoading ? (
        <LoadingRows />
      ) : isError ? (
        <ErrorState
          className="py-4"
          message="Certifications couldn't be loaded."
          onRetry={() => refetch()}
        />
      ) : certs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No certifications assigned yet.
          {isAdmin && (
            <>
              {" "}
              <button
                type="button"
                className="font-medium text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
                onClick={() => setAssignOpen(true)}
              >
                Assign one
              </button>
              .
            </>
          )}
        </p>
      ) : (
        <ul className="space-y-3">
          {certs.map((c) => {
            const expired = isExpiredDateOnly(c.expiresAt);
            return (
              <CertificationRow
                key={c.id}
                cert={c}
                expired={expired}
                personName={personName}
                isAdmin={isAdmin}
                onStatusChange={(status) => update.mutate({ id: c.id, status })}
                onProofUploaded={(fileId) => update.mutate({ id: c.id, proofFileId: fileId })}
                onRemove={() => setDeleteTarget(c)}
              />
            );
          })}
        </ul>
      )}

      {isAdmin && (
        <PersonCertificationAssignDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          personId={personId}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          onOpenChange={() => setDeleteTarget(null)}
          title="Remove certification?"
          description={`Removes ${deleteTarget.certificationName} from this person. Any uploaded proof file is preserved (delete the file separately if needed).`}
          confirmLabel="Remove"
          destructive
          onConfirm={() => {
            remove.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
          }}
        />
      )}
    </DetailSection>
  );
}

function CertificationRow({
  cert,
  expired,
  personName,
  isAdmin,
  onStatusChange,
  onProofUploaded,
  onRemove,
}: {
  cert: PersonCertificationRow;
  expired: boolean;
  personName: string;
  isAdmin: boolean;
  onStatusChange: (status: "assigned" | "started" | "completed") => void;
  onProofUploaded: (fileId: string) => void;
  onRemove: () => void;
}) {
  const { uploadFile, isUploading } = useFileUpload();

  async function handleViewProof() {
    if (!cert.proofFileId) return;
    const url = await fetchFileDownloadUrl(cert.proofFileId);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadFile(file);
    e.target.value = ""; // reset for next selection
    if (result) onProofUploaded(result.fileId);
  }

  return (
    <li className="rounded-md border bg-card p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Award className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Link
              to="/dashboard/certifications/$certificationId"
              params={{ certificationId: cert.certificationId }}
              search={originLinkSearch("person", cert.personId, personName, "certifications")}
              className="font-medium hover:underline"
            >
              {cert.certificationName}
            </Link>
            {cert.certificationIssuingBody && (
              <span className="text-xs text-muted-foreground">
                · {cert.certificationIssuingBody}
              </span>
            )}
            <StatusPill
              tone={statusToneMap[cert.status]}
              label={cert.status.charAt(0).toUpperCase() + cert.status.slice(1)}
            />
            {expired && <StatusPill tone="danger" label="Expired" />}
          </div>
          {cert.expiresAt && (
            <div className="mt-1 text-xs text-muted-foreground">
              Expires {formatDateOnly(cert.expiresAt)}
            </div>
          )}
          {cert.notes && <div className="mt-1 text-xs text-muted-foreground">{cert.notes}</div>}
          {cert.proofFileId && (
            <div className="mt-1 text-xs">
              <button
                type="button"
                className="font-medium text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
                onClick={handleViewProof}
              >
                View proof{cert.proofFilename ? `: ${cert.proofFilename}` : ""}
              </button>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isAdmin && cert.status !== "completed" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onStatusChange(cert.status === "assigned" ? "started" : "completed")}
            >
              {cert.status === "assigned" ? "Start" : "Mark complete"}
            </Button>
          )}
          {isAdmin && (
            <label className="cursor-pointer">
              <input
                type="file"
                className="hidden"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <span
                className={`inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground ${
                  isUploading ? "opacity-50" : ""
                }`}
              >
                <Upload className="h-3.5 w-3.5" />
                {isUploading ? "Uploading…" : cert.proofFileId ? "Replace" : "Upload proof"}
              </span>
            </label>
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRemove}
              className="text-destructive hover:text-destructive"
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
